import { onboardingPlanLead } from '../../utils/mealPlanHelpers.js'

const SKELETON_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const SKELETON_RAIL_LINES = [1, 2, 3, 4]

function SkeletonLine({ className = '' }) {
  return <div className={`planner-skeleton-line ${className}`.trim()} />
}

function SkeletonWeekNav() {
  return (
    <div className="planner-week-nav planner-skeleton-week-nav">
      <SkeletonLine className="planner-skeleton-week-nav-label" />
      <div className="planner-skeleton-week-nav-arrows">
        <SkeletonLine className="planner-skeleton-week-nav-arrow" />
        <SkeletonLine className="planner-skeleton-week-nav-arrow" />
      </div>
    </div>
  )
}

function SkeletonCookProgress() {
  return (
    <div className="pcp-card pcp-card--desktop pcp-card--strip planner-skeleton-pcp">
      <div className="pcp-top-row">
        <SkeletonLine className="planner-skeleton-pcp-eyebrow" />
      </div>
      <SkeletonLine className="planner-skeleton-pcp-headline" />
      <div className="pcs-strip" aria-hidden="true">
        {SKELETON_DAYS.map((day) => (
          <div key={day} className="pcs-day">
            <SkeletonLine className="planner-skeleton-pcs-dow" />
            <SkeletonLine className="planner-skeleton-pcs-bar" />
          </div>
        ))}
      </div>
      <div className="pcp-footer">
        <div className="planner-skeleton-pcp-meter">
          <div className="planner-skeleton-pcp-meter-label-row">
            <SkeletonLine className="planner-skeleton-pcp-meter-label" />
            <SkeletonLine className="planner-skeleton-pcp-meter-value" />
          </div>
          <SkeletonLine className="planner-skeleton-pcp-meter-track" />
        </div>
      </div>
    </div>
  )
}

function SkeletonMealRow() {
  return (
    <div className="meal-row planner-skeleton-meal-row">
      <div className="planner-skeleton-day-chip">
        <SkeletonLine className="planner-skeleton-day-chip-bar" />
        <SkeletonLine className="planner-skeleton-day-chip-label" />
        <SkeletonLine className="planner-skeleton-day-chip-date" />
      </div>
      <SkeletonLine className="planner-skeleton-meal-swatch" />
      <div className="planner-skeleton-meal-copy">
        <SkeletonLine className="planner-skeleton-meal-title" />
        <SkeletonLine className="planner-skeleton-meal-meta" />
      </div>
    </div>
  )
}

function SkeletonGroceryRail() {
  return (
    <aside className="grocery-rail planner-skeleton-rail">
      <SkeletonLine className="planner-skeleton-rail-title" />
      <SkeletonLine className="planner-skeleton-rail-pill" />
      {SKELETON_RAIL_LINES.map((line) => (
        <SkeletonLine
          key={line}
          className={`planner-skeleton-rail-list${line === SKELETON_RAIL_LINES.length ? ' short' : ''}`}
        />
      ))}
      <SkeletonLine className="planner-skeleton-rail-cta" />
    </aside>
  )
}

function OnboardingWeekFrame({ error = '', onRetry = null }) {
  const failed = Boolean(error)
  return (
    <div
      className="planner-page planner-page--onboarding planner-onboarding-state"
      role={failed ? 'alert' : 'status'}
      aria-live={failed ? 'assertive' : 'polite'}
      aria-busy={failed ? undefined : 'true'}
    >
      <div className="planner-onboarding-head">
        <div className="pwl-head-block">
          <div className="pwl-head">
            <div className="pwl-copy">
              <h1 className="pwl-title">Dinners</h1>
              <p className="pwl-value">{onboardingPlanLead()}</p>
            </div>
            {!failed ? <SkeletonLine className="planner-onboarding-state__plan-button" /> : null}
          </div>
        </div>
      </div>

      <div className="planner-week-list planner-week-list--onboarding planner-onboarding-state__week">
        <div className="pwl-strip" aria-hidden="true">
          <div className="planner-onboarding-state__days">
            {SKELETON_DAYS.map((day) => (
              <div key={day} className="planner-onboarding-state__day">
                <span>{day}</span>
                <span>–</span>
              </div>
            ))}
          </div>
        </div>
        {failed ? (
          <div className="planner-onboarding-state__error">
            <p>{error}</p>
            <button type="button" className="button secondary" onClick={onRetry}>
              Try again
            </button>
          </div>
        ) : (
          <div className="planner-week-rows" aria-hidden="true">
            <SkeletonMealRow />
            <SkeletonMealRow />
          </div>
        )}
      </div>
      {!failed ? <span className="sr-only">Loading this week</span> : null}
    </div>
  )
}

export function PlannerErrorState({ onRetry, onboardingMode = false }) {
  if (onboardingMode) {
    return <OnboardingWeekFrame error="Couldn’t load the week." onRetry={onRetry} />
  }
  return (
    <div className="planner-panel-error" role="alert">
      <p>Couldn’t load the week.</p>
      <button type="button" className="button secondary" onClick={onRetry}>
        Try again
      </button>
    </div>
  )
}

export default function PlannerSkeleton({ onboardingMode = false }) {
  if (onboardingMode) return <OnboardingWeekFrame />
  return (
    <div className="planner-page planner-skeleton" role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">Loading planner</span>

      <div className="planner-hero planner-skeleton-hero" aria-hidden="true">
        <div className="planner-hero-heading">
          <div className="planner-hero-title-block">
            <SkeletonLine className="planner-skeleton-eyebrow" />
            <SkeletonLine className="planner-skeleton-title" />
          </div>
          <SkeletonWeekNav />
        </div>
      </div>

      <div className="planner-grid planner-skeleton-grid" aria-hidden="true">
        <div className="planner-skeleton-main">
          <SkeletonCookProgress />
          <div className="planner-week-list planner-week-list--headed planner-skeleton-row-stack">
            <div className="pwl-head" aria-hidden="true">
              <SkeletonLine className="planner-skeleton-pwl-title" />
            </div>
            <div className="planner-week-rows">
              {SKELETON_DAYS.map((day) => (
                <SkeletonMealRow key={day} />
              ))}
            </div>
          </div>
        </div>

        <SkeletonGroceryRail />
      </div>
    </div>
  )
}
